import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LanguageContext = createContext(null);

// Default UI translations (static texts)
const uiTranslations = {
  pt: {
    nav: {
      home: "Início",
      about: "Sobre",
      services: "Serviços",
      celebrant: "Celebrante",
      simulator: "Simulador",
      tools: "Ferramentas",
      portfolio: "Portfólio",
      testimonials: "Testemunhos",
      contact: "Contacto",
      login: "Entrar"
    },
    hero: {
      title: "Alma Leve Weddings",
      subtitle: "Onde cada detalhe conta uma história de amor",
      tagline: "Planeamento de casamentos com alma, delicadeza e serenidade",
      cta: "Vamos planear o seu dia"
    },
    about: {
      label: "Conheça-me",
      title: "Sobre Mim",
      subtitle: "A pessoa por trás dos sonhos",
      badge: "Wedding Planner"
    },
    services: {
      label: "O que fazemos",
      title: "Serviços",
      subtitle: "Soluções pensadas para cada necessidade",
      learnMore: "Saber mais",
      included: "Incluído neste serviço",
      bookConsultation: "Agendar Reunião",
      features: "características"
    },
    portfolio: {
      label: "Nosso trabalho",
      title: "Portfólio",
      subtitle: "Histórias de amor que ajudámos a contar"
    },
    testimonials: {
      label: "Feedback",
      title: "Testemunhos",
      subtitle: "O que dizem os nossos casais"
    },
    blog: {
      label: "Inspiração",
      title: "Blog",
      subtitle: "Inspiração e dicas para o vosso dia",
      readMore: "Ler mais"
    },
    contact: {
      label: "Fale connosco",
      title: "Contacto",
      subtitle: "Vamos começar a planear o vosso dia",
      description: "Estou aqui para vos ouvir e perceber como posso ajudar a tornar o vosso casamento num dia inesquecível. Cada história é única e merece toda a atenção.",
      name: "Nome",
      namePlaceholder: "O vosso nome",
      email: "Email",
      emailPlaceholder: "email@exemplo.pt",
      phone: "Telefone",
      phonePlaceholder: "+351 900 000 000",
      weddingDate: "Data prevista do casamento",
      weddingDatePlaceholder: "Mês/Ano ou data específica",
      preferredTime: "Melhor horário para contacto",
      selectOption: "Selecione uma opção",
      morning: "Manhã (9h - 12h)",
      lunch: "Hora de almoço (12h - 14h)",
      afternoon: "Tarde (14h - 18h)",
      evening: "Final do dia (18h - 20h)",
      noPreference: "Sem preferência",
      message: "Mensagem",
      messagePlaceholder: "Contem-me um pouco sobre o vosso casamento e como posso ajudar...",
      send: "Enviar Mensagem",
      sending: "A enviar...",
      success: "Mensagem Enviada!",
      successMessage: "Obrigada pelo seu contacto. Entrarei em contacto consigo brevemente.",
      newMessage: "Enviar nova mensagem",
      error: "Ocorreu um erro. Por favor, tente novamente.",
      availability: "Disponível para casamentos em todo o país e destinos internacionais"
    },
    footer: {
      navigation: "Navegação",
      contact: "Contacto",
      rights: "Todos os direitos reservados",
      tagline: "Feito com",
      inPortugal: "em Portugal",
      description: "Transformamos sonhos em celebrações inesquecíveis. Com dedicação, carinho e atenção a cada detalhe, ajudamos casais a viver o dia mais especial das suas vidas com serenidade e alegria."
    },
    leadMagnet: {
      title: "Um presente para vocês!",
      subtitle: "Guias gratuitos para o vosso casamento",
      thanks: "Obrigada pelo vosso contacto! Como agradecimento, oferecemos estes guias exclusivos para vos ajudar no planeamento.",
      guide1Title: "10 Erros a Evitar no Planeamento",
      guide1Description: "Descubra os erros mais comuns que os casais cometem e como evitá-los para um casamento sem stress.",
      guide2Title: "Checklist Completa do Dia",
      guide2Description: "Uma lista detalhada de tudo o que precisam verificar no grande dia, hora a hora.",
      yourName: "O vosso nome",
      receive: "Receber Guias Gratuitos",
      sent: "Guias Enviados!",
      checkEmail: "Verifiquem o email",
      helpPlan: "Os guias vão ajudar-vos a planear o casamento perfeito!",
      noSpam: "Os guias serão enviados para o vosso email. Sem spam, prometemos!",
      selectOne: "Selecione pelo menos um guia"
    },
    common: {
      close: "Fechar",
      loading: "A carregar...",
      error: "Erro"
    }
  },
  en: {
    nav: {
      home: "Home",
      about: "About",
      services: "Services",
      celebrant: "Celebrant",
      simulator: "Simulator",
      tools: "Tools",
      portfolio: "Portfolio",
      testimonials: "Testimonials",
      contact: "Contact",
      login: "Login"
    },
    hero: {
      title: "Alma Leve Weddings",
      subtitle: "Where every detail tells a love story",
      tagline: "Wedding planning with soul, delicacy and serenity",
      cta: "Let's plan your day"
    },
    about: {
      label: "Meet me",
      title: "About Me",
      subtitle: "The person behind the dreams",
      badge: "Wedding Planner"
    },
    services: {
      label: "What we do",
      title: "Services",
      subtitle: "Solutions designed for every need",
      learnMore: "Learn more",
      included: "Included in this service",
      bookConsultation: "Book Meeting",
      features: "features"
    },
    portfolio: {
      label: "Our work",
      title: "Portfolio",
      subtitle: "Love stories we helped tell"
    },
    testimonials: {
      label: "Feedback",
      title: "Testimonials",
      subtitle: "What our couples say"
    },
    blog: {
      label: "Inspiration",
      title: "Blog",
      subtitle: "Inspiration and tips for your day",
      readMore: "Read more"
    },
    contact: {
      label: "Get in touch",
      title: "Contact",
      subtitle: "Let's start planning your day",
      description: "I'm here to listen and understand how I can help make your wedding an unforgettable day. Every story is unique and deserves all the attention.",
      name: "Name",
      namePlaceholder: "Your name",
      email: "Email",
      emailPlaceholder: "email@example.com",
      phone: "Phone",
      phonePlaceholder: "+351 900 000 000",
      weddingDate: "Expected wedding date",
      weddingDatePlaceholder: "Month/Year or specific date",
      preferredTime: "Best time to contact",
      selectOption: "Select an option",
      morning: "Morning (9am - 12pm)",
      lunch: "Lunch time (12pm - 2pm)",
      afternoon: "Afternoon (2pm - 6pm)",
      evening: "End of day (6pm - 8pm)",
      noPreference: "No preference",
      message: "Message",
      messagePlaceholder: "Tell me a bit about your wedding and how I can help...",
      send: "Send Message",
      sending: "Sending...",
      success: "Message Sent!",
      successMessage: "Thank you for your contact. I will get back to you shortly.",
      newMessage: "Send new message",
      error: "An error occurred. Please try again.",
      availability: "Available for weddings throughout the country and international destinations"
    },
    footer: {
      navigation: "Navigation",
      contact: "Contact",
      rights: "All rights reserved",
      tagline: "Made with",
      inPortugal: "in Portugal",
      description: "We transform dreams into unforgettable celebrations. With dedication, care and attention to every detail, we help couples live the most special day of their lives with serenity and joy."
    },
    leadMagnet: {
      title: "A gift for you!",
      subtitle: "Free guides for your wedding",
      thanks: "Thank you for your contact! As a thank you, we offer these exclusive guides to help you with your planning.",
      guide1Title: "10 Mistakes to Avoid in Planning",
      guide1Description: "Discover the most common mistakes couples make and how to avoid them for a stress-free wedding.",
      guide2Title: "Complete Day Checklist",
      guide2Description: "A detailed list of everything you need to check on the big day, hour by hour.",
      yourName: "Your name",
      receive: "Receive Free Guides",
      sent: "Guides Sent!",
      checkEmail: "Check your email",
      helpPlan: "The guides will help you plan the perfect wedding!",
      noSpam: "Guides will be sent to your email. No spam, we promise!",
      selectOne: "Select at least one guide"
    },
    common: {
      close: "Close",
      loading: "Loading...",
      error: "Error"
    }
  },
  fr: {
    nav: {
      home: "Accueil",
      about: "À propos",
      services: "Services",
      celebrant: "Célébrant",
      simulator: "Simulateur",
      tools: "Outils",
      portfolio: "Portfolio",
      testimonials: "Témoignages",
      contact: "Contact",
      login: "Connexion"
    },
    hero: {
      title: "Alma Leve Weddings",
      subtitle: "Où chaque détail raconte une histoire d'amour",
      tagline: "Organisation de mariages avec âme, délicatesse et sérénité",
      cta: "Planifions votre jour"
    },
    about: {
      label: "Me connaître",
      title: "À Propos",
      subtitle: "La personne derrière les rêves",
      badge: "Wedding Planner"
    },
    services: {
      label: "Ce que nous faisons",
      title: "Services",
      subtitle: "Solutions conçues pour chaque besoin",
      learnMore: "En savoir plus",
      included: "Inclus dans ce service",
      bookConsultation: "Réserver une réunion",
      features: "caractéristiques"
    },
    portfolio: {
      label: "Notre travail",
      title: "Portfolio",
      subtitle: "Histoires d'amour que nous avons aidé à raconter"
    },
    testimonials: {
      label: "Retours",
      title: "Témoignages",
      subtitle: "Ce que disent nos couples"
    },
    blog: {
      label: "Inspiration",
      title: "Blog",
      subtitle: "Inspiration et conseils pour votre jour",
      readMore: "Lire plus"
    },
    contact: {
      label: "Contactez-nous",
      title: "Contact",
      subtitle: "Commençons à planifier votre jour",
      description: "Je suis là pour vous écouter et comprendre comment je peux vous aider à faire de votre mariage un jour inoubliable. Chaque histoire est unique et mérite toute l'attention.",
      name: "Nom",
      namePlaceholder: "Votre nom",
      email: "Email",
      emailPlaceholder: "email@exemple.com",
      phone: "Téléphone",
      phonePlaceholder: "+351 900 000 000",
      weddingDate: "Date prévue du mariage",
      weddingDatePlaceholder: "Mois/Année ou date spécifique",
      preferredTime: "Meilleur moment pour vous contacter",
      selectOption: "Sélectionnez une option",
      morning: "Matin (9h - 12h)",
      lunch: "Déjeuner (12h - 14h)",
      afternoon: "Après-midi (14h - 18h)",
      evening: "Fin de journée (18h - 20h)",
      noPreference: "Sans préférence",
      message: "Message",
      messagePlaceholder: "Parlez-moi un peu de votre mariage et comment je peux vous aider...",
      send: "Envoyer le message",
      sending: "Envoi en cours...",
      success: "Message Envoyé!",
      successMessage: "Merci pour votre contact. Je vous répondrai dans les plus brefs délais.",
      newMessage: "Envoyer un nouveau message",
      error: "Une erreur s'est produite. Veuillez réessayer.",
      availability: "Disponible pour les mariages dans tout le pays et destinations internationales"
    },
    footer: {
      navigation: "Navigation",
      contact: "Contact",
      rights: "Tous droits réservés",
      tagline: "Fait avec",
      inPortugal: "au Portugal",
      description: "Nous transformons les rêves en célébrations inoubliables. Avec dévouement, soin et attention à chaque détail, nous aidons les couples à vivre le jour le plus spécial de leur vie avec sérénité et joie."
    },
    leadMagnet: {
      title: "Un cadeau pour vous!",
      subtitle: "Guides gratuits pour votre mariage",
      thanks: "Merci pour votre contact! En guise de remerciement, nous vous offrons ces guides exclusifs pour vous aider dans votre organisation.",
      guide1Title: "10 Erreurs à Éviter",
      guide1Description: "Découvrez les erreurs les plus courantes que font les couples et comment les éviter pour un mariage sans stress.",
      guide2Title: "Checklist Complète du Jour",
      guide2Description: "Une liste détaillée de tout ce que vous devez vérifier le grand jour, heure par heure.",
      yourName: "Votre nom",
      receive: "Recevoir les Guides Gratuits",
      sent: "Guides Envoyés!",
      checkEmail: "Vérifiez votre email",
      helpPlan: "Les guides vont vous aider à planifier le mariage parfait!",
      noSpam: "Les guides seront envoyés à votre email. Pas de spam, promis!",
      selectOne: "Sélectionnez au moins un guide"
    },
    common: {
      close: "Fermer",
      loading: "Chargement...",
      error: "Erreur"
    }
  },
  es: {
    nav: {
      home: "Inicio",
      about: "Sobre mí",
      services: "Servicios",
      celebrant: "Celebrante",
      simulator: "Simulador",
      tools: "Herramientas",
      portfolio: "Portafolio",
      testimonials: "Testimonios",
      contact: "Contacto",
      login: "Entrar"
    },
    hero: {
      title: "Alma Leve Weddings",
      subtitle: "Donde cada detalle cuenta una historia de amor",
      tagline: "Planificación de bodas con alma, delicadeza y serenidad",
      cta: "Planifiquemos tu día"
    },
    about: {
      label: "Conóceme",
      title: "Sobre Mí",
      subtitle: "La persona detrás de los sueños",
      badge: "Wedding Planner"
    },
    services: {
      label: "Lo que hacemos",
      title: "Servicios",
      subtitle: "Soluciones diseñadas para cada necesidad",
      learnMore: "Saber más",
      included: "Incluido en este servicio",
      bookConsultation: "Reservar Reunión",
      features: "características"
    },
    portfolio: {
      label: "Nuestro trabajo",
      title: "Portafolio",
      subtitle: "Historias de amor que ayudamos a contar"
    },
    testimonials: {
      label: "Opiniones",
      title: "Testimonios",
      subtitle: "Lo que dicen nuestras parejas"
    },
    blog: {
      label: "Inspiración",
      title: "Blog",
      subtitle: "Inspiración y consejos para tu día",
      readMore: "Leer más"
    },
    contact: {
      label: "Contacta con nosotros",
      title: "Contacto",
      subtitle: "Empecemos a planificar tu día",
      description: "Estoy aquí para escucharte y entender cómo puedo ayudarte a hacer de tu boda un día inolvidable. Cada historia es única y merece toda la atención.",
      name: "Nombre",
      namePlaceholder: "Tu nombre",
      email: "Email",
      emailPlaceholder: "email@ejemplo.com",
      phone: "Teléfono",
      phonePlaceholder: "+351 900 000 000",
      weddingDate: "Fecha prevista de la boda",
      weddingDatePlaceholder: "Mes/Año o fecha específica",
      preferredTime: "Mejor horario para contactarte",
      selectOption: "Selecciona una opción",
      morning: "Mañana (9h - 12h)",
      lunch: "Hora del almuerzo (12h - 14h)",
      afternoon: "Tarde (14h - 18h)",
      evening: "Final del día (18h - 20h)",
      noPreference: "Sin preferencia",
      message: "Mensaje",
      messagePlaceholder: "Cuéntame un poco sobre tu boda y cómo puedo ayudarte...",
      send: "Enviar Mensaje",
      sending: "Enviando...",
      success: "¡Mensaje Enviado!",
      successMessage: "Gracias por tu contacto. Me pondré en contacto contigo pronto.",
      newMessage: "Enviar nuevo mensaje",
      error: "Ha ocurrido un error. Por favor, inténtalo de nuevo.",
      availability: "Disponible para bodas en todo el país y destinos internacionales"
    },
    footer: {
      navigation: "Navegación",
      contact: "Contacto",
      rights: "Todos los derechos reservados",
      tagline: "Hecho con",
      inPortugal: "en Portugal",
      description: "Transformamos sueños en celebraciones inolvidables. Con dedicación, cariño y atención a cada detalle, ayudamos a las parejas a vivir el día más especial de sus vidas con serenidad y alegría."
    },
    leadMagnet: {
      title: "¡Un regalo para vosotros!",
      subtitle: "Guías gratuitas para vuestra boda",
      thanks: "¡Gracias por vuestro contacto! Como agradecimiento, os ofrecemos estas guías exclusivas para ayudaros con la planificación.",
      guide1Title: "10 Errores a Evitar",
      guide1Description: "Descubre los errores más comunes que cometen las parejas y cómo evitarlos para una boda sin estrés.",
      guide2Title: "Checklist Completa del Día",
      guide2Description: "Una lista detallada de todo lo que necesitáis verificar en el gran día, hora a hora.",
      yourName: "Vuestro nombre",
      receive: "Recibir Guías Gratuitas",
      sent: "¡Guías Enviadas!",
      checkEmail: "Comprobad el email",
      helpPlan: "¡Las guías os ayudarán a planificar la boda perfecta!",
      noSpam: "Las guías serán enviadas a vuestro email. ¡Sin spam, prometido!",
      selectOne: "Selecciona al menos una guía"
    },
    common: {
      close: "Cerrar",
      loading: "Cargando...",
      error: "Error"
    }
  }
};

const languages = [
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' }
];

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Get from localStorage or default to 'pt'
    return localStorage.getItem('language') || 'pt';
  });
  const [translationCache, setTranslationCache] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  // Save language preference
  useEffect(() => {
    localStorage.setItem('language', currentLanguage);
  }, [currentLanguage]);

  // Get UI translation
  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = uiTranslations[currentLanguage];
    
    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        // Fallback to Portuguese
        value = uiTranslations['pt'];
        for (const fallbackKey of keys) {
          if (value && value[fallbackKey]) {
            value = value[fallbackKey];
          }
        }
        break;
      }
    }
    
    return typeof value === 'string' ? value : key;
  }, [currentLanguage]);

  // Translate dynamic content
  const translateContent = useCallback(async (texts) => {
    if (currentLanguage === 'pt' || !texts || texts.length === 0) {
      return texts;
    }

    // Check cache
    const cacheKey = `${currentLanguage}_${texts.join('||')}`;
    if (translationCache[cacheKey]) {
      return translationCache[cacheKey];
    }

    setIsTranslating(true);
    
    try {
      const response = await fetch(`${API_URL}/api/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          texts,
          target_language: currentLanguage,
          source_language: 'pt'
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Cache the result
        setTranslationCache(prev => ({
          ...prev,
          [cacheKey]: data.translations
        }));
        return data.translations;
      }
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setIsTranslating(false);
    }

    return texts;
  }, [currentLanguage, translationCache]);

  // Change language
  const changeLanguage = useCallback((langCode) => {
    if (languages.find(l => l.code === langCode)) {
      setCurrentLanguage(langCode);
    }
  }, []);

  const value = {
    currentLanguage,
    languages,
    changeLanguage,
    t,
    translateContent,
    isTranslating,
    isPortuguese: currentLanguage === 'pt'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
